import { HistoryButton, useDocEvents } from "../team/collab.jsx";
const { useCallback, useEffect, useRef, useState } = React;
import { dbDeleteBoard, dbGetBoard, dbGetTemplate, dbGetWeekdayDefault, dbListTemplates, dbSetBoard, dbSubscribeLiveActive, dbSubscribeMonthBoards } from "../data.jsx";
import { BoardEditor } from "../editor/BoardEditor.jsx";
import { useT } from "../i18n.jsx";
import { WEEKDAYS, emptyBoard, fromKey, remapIds, thaiDate, toKey, weekdayOf, withBoardDefaults } from "../model.jsx";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, LayoutTemplate, Loader2, Lock, Radio, Save, StarIcon, T, Unlock } from "../theme.jsx";

// ============================================================= DAY EDITOR =====

function DayEditor({ date, setDate, onBack, perms, onGoLive }) {
  const { lang, t } = useT();
  const [board, setBoard] = useState(emptyBoard());
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved");
  const [copyMsg, setCopyMsg] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const [linkedTplName, setLinkedTplName] = useState(null);
  const saveTimer = useRef(null);
  const dateKey = toKey(date);
  const wd = weekdayOf(date);

  const load = useCallback(async () => {
    setLoading(true);
    const own = await dbGetBoard(dateKey);
    if (own) { setBoard(withBoardDefaults(own)); setLocked(true); }
    else {
      const def = await dbGetWeekdayDefault(wd.id);
      setBoard(def ? withBoardDefaults(def.board) : { ...emptyBoard(), name: wd.name, category: wd.category, code: wd.code });
      setLinkedTplName(def ? def.templateName : null);
      setLocked(false);
    }
    setSaveState("saved");
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateKey]);

  useEffect(() => { load(); }, [load]);
  useDocEvents("boards", dateKey, {
    onReload: load,
    onSaved: () => setSaveState("saved"),
    onConflict: () => { clearTimeout(saveTimer.current); setSaveState("conflict"); },
  });

  const persistLocked = useCallback((nextBoard) => {
    setSaveState("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const ok = await dbSetBoard(dateKey, nextBoard);
      setSaveState((s) => (ok ? "saved" : s === "conflict" ? s : "error"));
    }, 600);
  }, [dateKey]);

  const handleChange = (nextBoard) => {
    setBoard(nextBoard);
    setLocked(true);
    persistLocked(nextBoard);
  };

  const resetToMaster = async () => { await dbDeleteBoard(dateKey); load(); };

  const shiftDate = (n) => setDate((d) => { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; });

  // pick a template from the Template Pool and load it into this day
  const [tplList, setTplList] = useState(null); // null = picker closed
  const [tplPick, setTplPick] = useState("");
  const openTplPicker = async () => {
    setTplList([]);
    const list = await dbListTemplates();
    setTplList(list);
    setTplPick(list[0] ? list[0].id : "");
  };
  const applyTemplateHere = async () => {
    if (!tplPick) return;
    const tpl = await dbGetTemplate(tplPick);
    if (!tpl) { setCopyMsg(t("applyFailed")); setTimeout(() => setCopyMsg(""), 2500); return; }
    handleChange(withBoardDefaults(remapIds(tpl)));
    setTplList(null);
    setCopyMsg(`${tpl.name || ""} ${t("tplAppliedHere")}`); setTimeout(() => setCopyMsg(""), 4000);
  };

  const copyPreviousDay = async () => {
    const prev = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
    const prevKey = toKey(prev);
    let src = await dbGetBoard(prevKey);
    if (!src) { const def = await dbGetWeekdayDefault(weekdayOf(prev).id); src = def && def.board; }
    if (!src) { setCopyMsg(t("copyPrevDayEmpty")); setTimeout(() => setCopyMsg(""), 2500); return; }
    const next = withBoardDefaults(remapIds(src));
    handleChange(next);
    setCopyMsg(t("copyPrevDayDone")); setTimeout(() => setCopyMsg(""), 2800);
  };

  return (
    <>
      <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", borderBottom: `1px solid ${T.border}`, background: T.panel, textAlign: "center" }}>
        <button className="rb-btn" onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, border: "none", borderRadius: 8, padding: "7px 11px", cursor: "pointer", color: T.text, fontSize: 12, fontWeight: 700 }}><ArrowLeft size={14} /> {t("navCalendar")}</button>
        <button aria-label={t("a11yPrevDay")} className="rb-btn" onClick={() => shiftDate(-1)} style={{ background: T.panel2, border: "none", borderRadius: 8, padding: 7, cursor: "pointer", color: T.text }}><ChevronLeft size={15} /></button>
        <input aria-label={t("a11yDate")} type="date" value={dateKey} onChange={(e) => setDate(fromKey(e.target.value))} className="rb-input"
          style={{ background: T.panel2, border: "none", borderRadius: 8, padding: "6px 10px", fontFamily: "inherit", fontSize: 13, color: T.text, textAlign: "center" }} />
        <button aria-label={t("a11yNextDay")} className="rb-btn" onClick={() => shiftDate(1)} style={{ background: T.panel2, border: "none", borderRadius: 8, padding: 7, cursor: "pointer", color: T.text }}><ChevronRight size={15} /></button>
        <button className="rb-btn" onClick={() => setDate(new Date())} style={{ background: "none", border: "none", color: T.accentInk, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>{t("today")}</button>
        <div style={{ fontSize: 13, color: T.subtext }}>{thaiDate(date, lang)}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {copyMsg && <span style={{ fontSize: 12, color: T.accentInk }}>{copyMsg}</span>}
          <button className="rb-btn" onClick={copyPreviousDay} style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, color: T.text }}>
            <Copy size={12} /> {t("copyPrevDay")}
          </button>
          {perms.canMaster && (
            <button className="rb-btn" onClick={tplList ? () => setTplList(null) : openTplPicker} style={{ display: "flex", alignItems: "center", gap: 5, background: T.panel2, border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, color: T.text }}>
              <LayoutTemplate size={12} /> {t("useTemplateHere")}
            </button>
          )}
          {perms.canMaster && onGoLive && (
            <button className="rb-btn" onClick={() => onGoLive(dateKey)} style={{ display: "flex", alignItems: "center", gap: 5, background: T.danger, border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff" }}>
              <Radio size={12} /> {t("goLive")}
            </button>
          )}
          {locked && <HistoryButton col="boards" id={dateKey} perms={perms} />}
          <div role="status" style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: saveState === "error" || saveState === "conflict" ? T.danger : T.subtext }}>
            {saveState === "saving" ? <><Loader2 size={12} style={{ animation: "rb-spin 1s linear infinite" }} /> {t("saving")}</> : saveState === "conflict" ? t("saveConflict") : saveState === "error" ? t("saveFailed") : <><Save size={12} /> {t("saved")}</>}
          </div>
        </div>
      </div>

      {tplList && (
        <div style={{ padding: "8px 16px", background: T.panel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
          {tplList.length === 0 ? <span style={{ fontSize: 12, color: T.subtext }}>{t("loading")}</span> : (
            <>
              <select aria-label={t("useTemplateHere")} value={tplPick} onChange={(e) => setTplPick(e.target.value)}
                style={{ fontSize: 12.5, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px", background: T.panel2, color: T.text }}>
                {tplList.map((x) => <option key={x.id} value={x.id}>{x.name || x.id}</option>)}
              </select>
              <button className="rb-btn" onClick={applyTemplateHere} style={{ background: T.accent, color: T.accentText, border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {locked ? t("tplReplaceHere") : t("tplApplyHere")}
              </button>
              <button className="rb-btn" onClick={() => setTplList(null)} style={{ background: T.panel2, color: T.text, border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>{t("cancel")}</button>
            </>
          )}
        </div>
      )}

      <div style={{ padding: "8px 16px", background: locked ? T.warnBg : T.infoBg, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", textAlign: "center" }}>
        {locked ? (
          <>
            <Lock size={13} color={T.warnText} />
            <span style={{ fontSize: 12, color: T.warnText, fontWeight: 600 }}>{t("lockedBanner")}</span>
            {perms.canMaster && (
              confirmReset ? (
                <span style={{ display: "flex", gap: 6 }}>
                  <span style={{ fontSize: 11.5, color: T.warnText }}>{t("confirmResetQ")}</span>
                  <button className="rb-btn" onClick={() => { setConfirmReset(false); resetToMaster(); }} style={{ fontSize: 11.5, color: T.danger, background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>{t("confirm")}</button>
                  <button className="rb-btn" onClick={() => setConfirmReset(false)} style={{ fontSize: 11.5, color: T.subtext, background: "none", border: "none", cursor: "pointer" }}>{t("cancel")}</button>
                </span>
              ) : (
                <button className="rb-btn" onClick={() => setConfirmReset(true)} style={{ fontSize: 11.5, color: T.accentInk, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>{t("resetToMaster")}</button>
              )
            )}
          </>
        ) : (
          <>
            <Unlock size={13} color={T.infoText} />
            <span style={{ fontSize: 12, color: T.infoText }}>{linkedTplName
              ? <>{t("unlockedBannerTplPrefix")} <b>{linkedTplName}</b> {t("unlockedBannerTplMid")} {lang === "en" ? wd.labelEn : wd.label} — {t("unlockedBannerSuffix")}</>
              : <>{t("unlockedBannerPrefix")} {lang === "en" ? wd.labelEn : wd.label} ({wd.code}) — {t("unlockedBannerSuffix")}</>}</span>
          </>
        )}
      </div>

      {loading ? <div style={{ padding: 50, textAlign: "center", color: T.subtext }}>{t("loading")}</div> :
        <BoardEditor key={dateKey} board={board} onChange={handleChange} perms={perms} />}
    </>
  );
}

// =============================================================== CALENDAR (gateway) =====

function CalendarScreen({ perms, onGotoMaster, onGotoTemplate, onGoLive, initialDate }) {
  const [viewMonth, setViewMonth] = useState(initialDate || new Date());
  const [selectedDate, setSelectedDate] = useState(initialDate || null);

  if (selectedDate) {
    return <DayEditor date={selectedDate} setDate={setSelectedDate} onBack={() => setSelectedDate(null)} perms={perms} onGoLive={onGoLive} />;
  }
  return (
    <MonthGrid
      month={viewMonth}
      onShiftMonth={(n) => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + n, 1))}
      onSelectDay={(d) => setSelectedDate(d)}
      onGotoMaster={onGotoMaster}
      onGotoTemplate={onGotoTemplate}
      onGoLive={onGoLive ? (dateKeyStr) => onGoLive(dateKeyStr) : null}
    />
  );
}

const THAI_MONTHS = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const ENGLISH_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAY_SHORT = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
const WEEKDAY_SHORT_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function MonthGrid({ month, onShiftMonth, onSelectDay, onGotoMaster, onGotoTemplate, onGoLive }) {
  const { lang, t } = useT();
  const [lockedDates, setLockedDates] = useState({});
  const [liveDates, setLiveDates] = useState({});
  const year = month.getFullYear(), mo = month.getMonth();
  const firstDow = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const today = new Date();
  const isToday = (d) => d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();

  // locked (has its own board doc) — live-updates as anyone locks/resets a day this month
  useEffect(() => {
    const startKey = toKey(new Date(year, mo, 1));
    const endKey = toKey(new Date(year, mo + 1, 1));
    const unsub = dbSubscribeMonthBoards(startKey, endKey, (snap) => {
      const m = {};
      snap.docs.forEach((d) => { m[d.id] = true; });
      setLockedDates(m);
    }, () => {});
    return unsub;
  }, [year, mo]);

  // which date(s) are currently live — global, but normally 0-1 at a time
  useEffect(() => {
    const unsub = dbSubscribeLiveActive((snap) => {
      const m = {};
      snap.docs.forEach((d) => { m[d.id] = true; });
      setLiveDates(m);
    }, () => {});
    return unsub;
  }, []);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, mo, d));

  return (
    <div style={{ background: "#0A0A0A", minHeight: "100vh", padding: "22px 14px 40px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)", width: 420, height: 420, background: "radial-gradient(circle, rgba(255,90,31,0.35), transparent 68%)", filter: "blur(10px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, right: -60, width: 320, height: 320, background: "radial-gradient(circle, rgba(255,90,31,0.22), transparent 70%)", filter: "blur(10px)", pointerEvents: "none" }} />

      <div style={{ position: "relative", textAlign: "center", marginBottom: 18 }}>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", letterSpacing: 0.3 }}>{t("calendarTitle")}</div>
        <div style={{ fontSize: 12, color: "#B9B7AE", marginTop: 2 }}>{t("calendarSubtitle")}</div>
      </div>

      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 16 }}>
        <button aria-label={t("a11yPrevMonth")} className="rb-btn" onClick={() => onShiftMonth(-1)} style={{ background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 4, padding: 8, color: "#fff", cursor: "pointer" }}><ChevronLeft size={16} /></button>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", minWidth: 160 }}>{lang === "en" ? `${ENGLISH_MONTHS[mo]} ${year}` : `${THAI_MONTHS[mo]} ${year + 543}`}</div>
        <button aria-label={t("a11yNextMonth")} className="rb-btn" onClick={() => onShiftMonth(1)} style={{ background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 4, padding: 8, color: "#fff", cursor: "pointer" }}><ChevronRight size={16} /></button>
      </div>

      <div style={{ position: "relative", display: "flex", justifyContent: "center", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        <button className="rb-btn" onClick={onGotoMaster} style={{ display: "flex", alignItems: "center", gap: 6, background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 4, padding: "8px 14px", color: "#FF5A1F", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
          <StarIcon size={13} /> {t("masterTemplateBtn")}
        </button>
        <button className="rb-btn" onClick={onGotoTemplate} style={{ display: "flex", alignItems: "center", gap: 6, background: "#1A1A1A", border: "1px solid #2C2C2C", borderRadius: 4, padding: "8px 14px", color: "#FF5A1F", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
          <LayoutTemplate size={13} /> {t("templatePoolBtn")}
        </button>
      </div>

      <div style={{ position: "relative", maxWidth: 620, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
          {(lang === "en" ? WEEKDAY_SHORT_EN : WEEKDAY_SHORT).map((w) => (
            <div key={w} style={{ textAlign: "center", fontSize: 11, fontWeight: 800, color: "#7A786F" }}>{w}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const key = toKey(d);
            const wd = WEEKDAYS[d.getDay()];
            const today_ = isToday(d);
            const locked = lockedDates[key];
            const isLive = liveDates[key];
            return (
              <button key={i} className="rb-btn" onClick={() => (isLive && onGoLive ? onGoLive(key) : onSelectDay(d))}
                style={{
                  aspectRatio: "1 / 1", borderRadius: 4, cursor: "pointer", padding: 4,
                  background: isLive ? "#C1272D" : today_ ? "#FF5A1F" : "#161616",
                  border: isLive ? "1px solid #FF6B62" : today_ ? "1px solid #FF5A1F" : "1px solid #262626",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
                  color: "#fff", position: "relative",
                }}>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{d.getDate()}</span>
                <span style={{ fontSize: 7.5, fontWeight: 700, opacity: 0.8, lineHeight: 1 }}>{isLive ? "● LIVE" : wd.code}</span>
                {locked && !isLive && <span style={{ position: "absolute", top: 3, right: 3, width: 5, height: 5, borderRadius: "50%", background: today_ ? "#161513" : "#FF5A1F" }} />}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ position: "relative", textAlign: "center", marginTop: 18, fontSize: 11, color: "#7A786F" }}>
        {t("calendarFootnote")}
      </div>
    </div>
  );
}

export { DayEditor, CalendarScreen, THAI_MONTHS, ENGLISH_MONTHS, WEEKDAY_SHORT, WEEKDAY_SHORT_EN, MonthGrid };
